import { Check, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export interface SelectMenuOption {
  /** What is submitted — a node id for a cascade, the label itself for a plain list. */
  value: string;
  /** What the applicant reads. */
  label: string;
}

/**
 * Panel height cap, in pixels.
 *
 * A number rather than a Tailwind class because the flip-up decision has to compare it against the
 * space available — a class name cannot be measured without reading back computed styles. 280px is
 * about six 44px rows: enough to show that a long list scrolls, without the panel covering the whole
 * phone screen.
 */
const PANEL_MAX_HEIGHT = 280;

/** How long a typeahead buffer survives between keystrokes. */
const TYPEAHEAD_RESET_MS = 700;

/**
 * THE dropdown — a designed listbox, replacing the browser's native `<select>`.
 *
 * WHY NOT `<select>`. A native select cannot be styled past its border: the option list is drawn by
 * the operating system, so it ignores the product's font, radius, colours and spacing entirely — a
 * grey list on Windows, a translucent panel on macOS, a full-screen dialog on Android. On the
 * applicant's phone, the one screen that carries the brand, the dropdown was the only control that
 * looked like it belonged to a different application.
 *
 * WHAT IS GIVEN UP, HONESTLY. The native mobile picker is genuinely good — a big touch-friendly
 * sheet with momentum scrolling and, on some platforms, type-to-search. Replacing it means earning
 * that back: 44px rows (Apple's minimum touch target), a capped scroll area, keyboard typeahead, and
 * the active option always scrolled into view.
 *
 * ABSOLUTE, NOT A PORTAL. The panel is positioned inside its own container rather than fixed to the
 * viewport. A portal escapes every clipping ancestor, which sounds strictly better — but the builder
 * renders this questionnaire inside a PHONE MOCKUP, and a fixed-position panel would break out of
 * the phone frame and float over the page. Staying in flow keeps the preview honest; the flip-up
 * below handles the clipping case a portal would have solved.
 *
 * FOCUS STAYS ON THE TRIGGER, with `aria-activedescendant` naming the active row. That is the ARIA
 * combobox pattern, and it is why the rows are not buttons: moving real focus into the list would
 * mean the trigger loses it, and every arrow key would then have to be re-plumbed back out.
 */
export function SelectMenu({
  options,
  value,
  placeholder,
  disabled = false,
  ariaLabel,
  className,
  onChange,
}: {
  options: readonly SelectMenuOption[];
  /** The selected option's `value`, or `''` for none. */
  value: string;
  placeholder: string;
  disabled?: boolean;
  /** Needed when no visible `<label>` is tied to this control. */
  ariaLabel?: string;
  className?: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  /** True when the panel must open upwards rather than down. */
  const [dropUp, setDropUp] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const hasSelection = value.length > 0 && options.some((option) => option.value === value);

  /**
   * The rows actually rendered: the options, preceded by a CLEAR row once something is chosen.
   *
   * One array for rendering AND for keyboard navigation, so an index always means the same thing.
   * Keeping the clear row outside the list would make it unreachable by arrow keys — which is how a
   * mandatory-looking field ends up impossible to un-answer with a keyboard.
   *
   * It appears only when there is a selection: a native select needed a permanent empty row to
   * express "not answered", but the placeholder already says that, and an always-present blank row
   * at the top of every list is a row people hit by accident.
   */
  const rows = useMemo<SelectMenuOption[]>(
    () => (hasSelection ? [{ value: '', label: placeholder }, ...options] : [...options]),
    [hasSelection, options, placeholder],
  );

  /** Index of the first real option — 1 when the clear row is present, 0 otherwise. */
  const firstOptionIndex = hasSelection ? 1 : 0;
  const selectedIndex = hasSelection ? rows.findIndex((row) => row.value === value) : -1;
  const selected = selectedIndex >= 0 ? rows[selectedIndex] : undefined;

  // An empty list is not an error — a cascade tier whose parent is unchosen has nothing to offer
  // yet — but it must not be openable.
  const unusable = disabled || options.length === 0;

  const close = useCallback((focusTrigger: boolean) => {
    setOpen(false);
    setActiveIndex(-1);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  /**
   * Opens upwards when there is not enough room below and more room above.
   *
   * `useLayoutEffect` so the decision is made BEFORE the browser paints — deciding in a passive
   * effect draws the panel in the wrong place for one frame, which reads as a flicker.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    setDropUp(below < PANEL_MAX_HEIGHT && rect.top > below);
  }, [open]);

  /** Opening lands on the current selection, or the first real option when there is none. */
  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstOptionIndex);
  }, [open, selectedIndex, firstOptionIndex]);

  /** Keeps the active row visible while arrowing through a long list. */
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  /**
   * Closes on a pointer press elsewhere, on an ancestor scroll, and on resize.
   *
   * `pointerdown` rather than `click`: it fires before focus moves, and a drag that starts outside
   * should close too. Scrolling closes because the panel is positioned against a trigger that has
   * just moved — following it would mean recomputing every scroll frame for a menu the user has
   * evidently stopped looking at.
   */
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    const onScroll = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    const onResize = () => close(false);

    document.addEventListener('pointerdown', onPointerDown);
    // Capture, so a scroll inside a nested container is seen too — the questionnaire body is one.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  function commit(index: number) {
    const row = rows[index];
    if (!row) return;
    onChange(row.value);
    close(true);
  }

  /**
   * Type-to-jump.
   *
   * The one thing a native select does that a hand-built list usually loses, and the difference
   * between picking "Uttarakhand" in one keystroke and scrolling past 35 states. The buffer resets
   * after a pause, so "ma" finds Maharashtra while a later "m" starts again.
   */
  const typeahead = useRef({ buffer: '', at: 0 });
  function jumpTo(char: string) {
    const now = Date.now();
    const continuing = now - typeahead.current.at <= TYPEAHEAD_RESET_MS;
    const buffer = continuing ? typeahead.current.buffer + char : char;
    typeahead.current = { buffer, at: now };

    // A fresh single letter starts looking AFTER the active row, so pressing "m" repeatedly cycles
    // through everything starting with m instead of sticking on the first match. A longer buffer
    // starts at the active row, because "ma" should still match where "m" landed.
    const from = buffer.length === 1 ? activeIndex + 1 : activeIndex;
    const count = options.length;

    for (let step = 0; step < count; step += 1) {
      // Searched over the REAL options only, wrapping within them — the clear row is not something
      // anybody types their way to.
      const index =
        firstOptionIndex + ((((from - firstOptionIndex + step) % count) + count) % count);
      if (rows[index]?.label.toLowerCase().startsWith(buffer)) {
        setActiveIndex(index);
        return;
      }
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (unusable) return;

    if (!open) {
      if (event.key === 'Enter' || event.key === ' ' || event.key.startsWith('Arrow')) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        return;
      case 'Tab':
        // Deliberately not prevented — Tab should move on, it just must not leave a panel open.
        close(false);
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        return;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % rows.length);
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + rows.length) % rows.length);
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(rows.length - 1);
        return;
      default:
        if (event.key.length === 1 && /\S/.test(event.key)) {
          event.preventDefault();
          jumpTo(event.key.toLowerCase());
        }
    }
  }

  return (
    <div ref={rootRef} className={cn('relative w-full', className)}>
      <button
        ref={triggerRef}
        type="button"
        // `combobox` + `aria-haspopup="listbox"` is the pattern for a value chooser, announced as
        // "collapsed / expanded, <value> selected" rather than as the command set `menu` implies.
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        disabled={unusable}
        onClick={() => !unusable && setOpen((prev) => !prev)}
        onKeyDown={onKeyDown}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3.5 py-2.5 text-left text-[14px] outline-none transition-colors',
          'disabled:cursor-default disabled:bg-[#F9FAFB]',
          open ? 'border-primary ring-2 ring-primary/10' : 'border-placeholder',
          !unusable && 'cursor-pointer hover:border-[#94A3B8]',
          // Focus-visible only, so a mouse click does not leave a ring behind on the trigger.
          'focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/10',
        )}
      >
        {/* `min-w-0` + `truncate`: a long option name must shorten rather than push the chevron out
            of the control, which is how this breaks on a narrow phone. */}
        <span
          className={cn('min-w-0 flex-1 truncate', selected ? 'text-[#0F172A]' : 'text-[#8E8E8E]')}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 text-[#64748B] transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel ?? placeholder}
          style={{ maxHeight: PANEL_MAX_HEIGHT }}
          className={cn(
            'absolute z-50 w-full overflow-y-auto overscroll-contain rounded-lg border border-[#E2E8F0] bg-white py-1 shadow-lg',
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {rows.map((row, index) => {
            const isClear = hasSelection && index === 0;
            const isSelected = index === selectedIndex;
            const isActive = index === activeIndex;

            return (
              // No key handler on the row: keyboard navigation for the whole listbox lives on the
              // trigger, per the aria-activedescendant pattern, so focus never enters here.
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: option values may legitimately repeat in stored data, so position is the only reliable identity for a row.
                key={`${row.value}-${index}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={isSelected}
                // -1 so the row is programmatically focusable for assistive tech without entering
                // the tab order, which stays on the trigger.
                tabIndex={-1}
                // `onMouseDown`, not `onClick`: the document-level `pointerdown` listener above
                // would otherwise close the panel before a click could land.
                onMouseDown={(event) => {
                  event.preventDefault();
                  commit(index);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between gap-2 px-3.5 py-2 text-left transition-colors',
                  // 44px is Apple's minimum touch target, and this list exists to be tapped. The
                  // clear row is deliberately shorter and quieter — it is an escape hatch, not a
                  // choice with equal weight.
                  isClear ? 'min-h-[38px] text-[13px] text-[#8E8E8E]' : 'min-h-[44px] text-[14px]',
                  isActive ? 'bg-[#F1F5F9]' : 'bg-white',
                  !isClear && (isSelected ? 'font-medium text-primary' : 'text-[#0F172A]'),
                )}
              >
                <span className="min-w-0 flex-1 break-words">{row.label}</span>
                {isSelected && <Check size={15} className="shrink-0 text-primary" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
